// The main screen: folders, and every board in the workspace.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api.js";
import { BoardBrowser } from "../components/BoardBrowser.jsx";
import { EmptyState, ErrorNote, PageHead, Shell } from "../components/Shell.jsx";
import { useDialogs } from "../components/dialogs.jsx";
import { Menu } from "../ui/Menu.jsx";
import { Plus } from "../ui/icons.jsx";
import { BoardRowsSkeleton, FolderCardsSkeleton } from "../ui/Skeleton.jsx";
import { plural } from "../ui/format.js";

export const HomePage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [folders, boards] = await Promise.all([api.listFolders(), api.allBoards()]);
      // Set together, so the page never renders half-loaded.
      setData({ folders: folders.folders, boards: boards.boards });
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const { open, element } = useDialogs({ folders: data?.folders ?? [], onChanged: load, navigate });

  const loading = data === null;
  const folders = data?.folders ?? [];

  return (
    <Shell
      actions={
        <>
          <button className="btn" onClick={() => open("newFolder")} disabled={loading}>
            New folder
          </button>
          <button
            className="btn primary"
            onClick={() => open("newBoard")}
            disabled={loading || folders.length === 0}
          >
            <Plus />
            New board
          </button>
        </>
      }
    >
      <ErrorNote error={error} onDismiss={() => setError(null)} />
      <PageHead title="Boards" lede="Everyone signed in can open and edit any board." />

      <section className="section">
        <div className="section-head">
          <h2 className="section-title">Folders</h2>
        </div>

        {loading ? (
          <FolderCardsSkeleton />
        ) : folders.length === 0 ? (
          <EmptyState
            title="No folders yet"
            hint="Start with one for a client or a project."
            action={
              <button className="btn primary" onClick={() => open("newFolder")}>
                New folder
              </button>
            }
          />
        ) : (
          <div className="folder-grid">
            {folders.map((folder) => (
              <div key={folder.id} className="folder-card">
                <span
                  className="folder-icon"
                  style={folder.color ? { background: folder.color } : undefined}
                />
                <Link to={`/f/${folder.id}`} className="truncate" style={{ flex: 1, minWidth: 0 }}>
                  <span className="folder-name truncate">{folder.name}</span>
                  <span className="muted small" style={{ display: "block" }}>
                    {plural(folder.boardCount ?? 0, "board")}
                  </span>
                </Link>
                <Menu
                  label={`Actions for ${folder.name}`}
                  items={[
                    {
                      label: "New board here",
                      onSelect: () => open("newBoard", null, { folderId: folder.id }),
                    },
                    { label: "Rename", onSelect: () => open("renameFolder", folder) },
                    "separator",
                    { label: "Delete", danger: true, onSelect: () => open("deleteFolder", folder) },
                  ]}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <div className="section-head">
          <h2 className="section-title">All boards</h2>
        </div>

        {loading ? (
          <BoardRowsSkeleton />
        ) : (
          <BoardBrowser
            boards={data.boards}
            showFolder
            actions={{
              onRename: (board) => open("editBoard", board),
              onEditTags: (board) => open("editBoard", board, { focus: "tags" }),
              onMove: (board) => open("move", board),
              onDelete: (board) => open("deleteBoard", board),
            }}
            emptyState={
              <EmptyState
                title="No boards yet"
                hint="An infinite canvas. Draw on it together, or let an agent draw on it."
                action={
                  <button
                    className="btn primary"
                    onClick={() => open("newBoard")}
                    disabled={folders.length === 0}
                  >
                    New board
                  </button>
                }
              />
            }
          />
        )}
      </section>

      {element}
    </Shell>
  );
};
